<template>
    <div>
        <table
            v-if="certificates.length > 0"
            class="kgr-table kgr-table--striped kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">{{ $t('filename') }}</th>
                    <th class="kgr-table__cell kgr-table__cell--headline">{{ $t('createdAt') }}</th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions">{{ $t('action') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="certificate in certificates"
                    class="kgr-table__row"
                    :key="certificate.type + certificate.id"
                >
                    <td class="kgr-table__cell">
                        <a
                            :href="getDownloadUrl(certificate)"
                            class="kgr-link kgr-link--unobtrusive"
                            @click.prevent="download(certificate)"
                        >
                            {{ certificate.filename }}
                        </a>
                    </td>
                    <td class="kgr-table__cell">{{ localeDate(certificate.created) }} {{ certificate.creator ? '(' + certificate.creator.firstname + ' ' +  certificate.creator.lastname + ')' : '' }}</td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <kgr-icon
                            icon-class="email"
                            :green-dot="certificate.sent !== null"
                            class="kgr-table__action"
                            :title="!certificate.email ? $t('Für den Empfänger ist keine E-Mail-Adresse hinterlegt') : $t('Bescheinigung per E-Mail versenden')"
                            accent
                            :disabled="!certificate.email"
                            @click="showEmailModal(certificate)"
                        />
                        <a
                            :href="getDownloadUrl(certificate)"
                            :title="$t('file.browser.download')"
                            class="kgr-table__action"
                            @click.prevent="download(certificate)"
                        >
                            <kgr-icon
                                icon-class="pdf"
                            />
                        </a>
                        <a
                            :href="getBrowserViewUrl(certificate)"
                            :title="$t('file.browser.view')"
                            target="_blank"
                            class="kgr-table__action"
                            @click.prevent="preview(certificate)"
                        >
                            <kgr-icon
                                icon-class="preview"
                            />
                        </a>
                        <kgr-icon-delete :title="$t('Bescheinigung löschen')" @click="showRemoveModal(certificate)"  class="kgr-table__action" />
                    </td>
                </tr>

            </tbody>
        </table>
        <kgr-loading v-if="loading" />
        <div v-else-if="recipientIdentifier && certificates.length === 0" v-t="'Für den gewählten Rechnungsempfänger liegen keine Bescheinigungen vor.'"></div>

        <kgr-generic-modal
            v-if="removeModalOpen"
            :value="removeModalOpen"
            :headline="$t('delete')"
            @close="closeRemoveModal"
        >
            {{ $t('confirmDeleteMessage', {objectName: deleteCertificate.filename}) }}
            <template #footer>
                <kgr-button
                    class="kgr-col kgr-button--outline float-left"
                    @click="closeRemoveModal"
                >
                    {{ $t('cancel') }}
                </kgr-button>
                <kgr-button
                    class="kgr-col"
                    :pending="deleting"
                    @click="removeCertificate"
                >
                    {{ $t('delete') }}
                </kgr-button>
            </template>
        </kgr-generic-modal>

        <kgr-generic-modal
            v-if="emailModalOpen"
            :value="emailModalOpen"
            :headline="$t('Per E-Mail senden')"
            @close="closeEmailModal"
        >
            {{ $t('popup.certificate.sendEmail.text') }}
            <template #footer>
                <kgr-button
                    class="kgr-col kgr-button--outline float-left"
                    @click="closeEmailModal"
                >
                    {{ $t('cancel') }}
                </kgr-button>
                <kgr-button
                    class="kgr-col"
                    :pending="emailing"
                    @click="sendEmail"
                >
                    {{ $t('ok.ok') }}
                </kgr-button>
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import CertificateRepository from '../../../js/repository/CertificateRepository.js';
import GenericModal from '../../common/modal/GenericModal.vue';
import Icon from '../../common/icon/Icon.vue';
import IconDelete from '../../common/icon/IconDelete.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import Loading from '../../common/Loading.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-generic-modal': GenericModal,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-icon': Icon,
        'kgr-loading': Loading,
    },
    props: {
        childId: {
            type: Number,
            required: true,
        },
        recipientIdentifier: {
            type: String,
            required: false,
            default: null,
        },
        certificates: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            removeModalOpen: false,
            emailModalOpen: false,
            deleting: false,
            emailing: false,
            deleteCertificate: null,
            emailCertificate: null,
        }
    },
    methods: {
        getDownloadUrl(certificate) {
            if(certificate.type === 'tax') {
                return `/api/certificates/tax/${certificate.id}/attachment`
            }
            return `/api/certificates/cost/${certificate.id}/attachment`
        },
        getBrowserViewUrl(certificate) {
            if(certificate.type === 'tax') {
                return `/api/certificates/tax/${certificate.id}/inline`
            }
            return `/api/certificates/cost/${certificate.id}/inline`
        },
        download(certificate) {
            CertificateRepository.downloadPdf(certificate)
                .catch(error => this.notifyPdfFailure(error));
        },
        preview(certificate) {
            // Opened up front so the click still counts as the user gesture a popup blocker looks for; the blob
            // only exists once the response is in.
            const tab = window.open('', '_blank');

            CertificateRepository.fetchPdf(certificate, 'inline')
                .then(response => {
                    const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    if (tab) {
                        tab.location.href = objectUrl;

                        return;
                    }
                    window.open(objectUrl, '_blank');
                })
                .catch(error => {
                    if (tab) {
                        tab.close();
                    }
                    this.notifyPdfFailure(error);
                });
        },
        notifyPdfFailure(error) {
            // Either shape reaches here: an Error model carries status directly, a raw axios error (non-JSON body)
            // carries it on the response.
            const status = error?.status ?? error?.response?.status;
            if (status === 404) {
                this.$notify.error('Die Datei ist nicht mehr vorhanden. Bitte löschen Sie die Bescheinigung und erzeugen Sie sie neu.');

                return;
            }
            this.$notify.error('saveChanges.error');
        },
        localeDate (Ymd) {
            return new Date(Ymd).toLocaleDateString();
        },
        closeEmailModal() {
            this.emailCertificate = null;
            this.emailModalOpen = false;
        },
        showEmailModal(certificate) {
            if(!certificate.email) {
                return;
            }
            this.emailCertificate = certificate;
            this.emailModalOpen = true;
        },
        closeRemoveModal() {
            this.deleteCertificate = null;
            this.removeModalOpen = false;
        },
        showRemoveModal(certificate) {
            this.deleteCertificate = certificate;
            this.removeModalOpen = true;
        },
        removeCertificate() {
            this.deleting = true;
            CertificateRepository.remove(this.deleteCertificate)
                .then(() => {
                    this.$emit('change');
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.deleting = false;
                    this.closeRemoveModal();
                })
        },
        sendEmail() {
            this.emailing = true;
            const url = this.emailCertificate.type === 'tax'
                ? `/certificates/tax/${this.emailCertificate.id}/email`
                : `/certificates/cost/${this.emailCertificate.id}/email`;

            axios.post(url)
                .then(() => {
                    this.$notify.success('Die E-Mail wurde versendet.');
                    this.$emit('change');
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.emailing = false;
                    this.closeEmailModal();
                })
            ;
        }
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
